import { memo, useCallback, useMemo, useRef, useState, type KeyboardEvent } from 'react';

import SearchBar from '~/shared/components/SearchBar';
import FilterScrollableList from './FilterScrollableList';
import SearchFilterOptionRow from './search/common/SearchFilterOptionRow';
import Typography from '~/shared/components/Typography';

import type { CheckboxFilterOption } from './checkboxFilterOption';
import { useFilterListKeyboardNav } from './hooks/useFilterListKeyboardNav';

interface FilterCheckboxListProps {
  options: CheckboxFilterOption[];
  globalSearchQuery?: string;
  showSectionSearch?: boolean;
  isDisclosureOpen?: boolean;
  isSelected: (key: string) => boolean;
  onToggle: (key: string) => void;
  tagVariant?: 'cluster' | 'instance' | 'volume' | 'node' | 'default';
  showCount?: boolean;
  sectionSearchInputId?: string;
  sectionSearchAriaLabel?: string;
}

function matchesMergedSearch(
  label: string,
  globalQuery?: string,
  sectionQuery?: string
): boolean {
  const labelLower = label.toLowerCase();
  const g = globalQuery?.trim().toLowerCase();
  const s = sectionQuery?.trim().toLowerCase();
  if (g && !labelLower.includes(g)) return false;
  if (s && !labelLower.includes(s)) return false;
  return true;
}

export function filterOptionsByQuery(
  options: CheckboxFilterOption[],
  searchQuery: string
): CheckboxFilterOption[] {
  const q = searchQuery.trim().toLowerCase();
  if (!q) return options;
  return options.filter((option) => option.label.toLowerCase().includes(q));
}

function filterOptionsByMergedQuery(
  options: CheckboxFilterOption[],
  globalSearchQuery?: string,
  sectionSearchQuery?: string
): CheckboxFilterOption[] {
  return options.filter((option) =>
    matchesMergedSearch(option.label, globalSearchQuery, sectionSearchQuery)
  );
}

function getFilterListFocusOnOpen(
  showSectionSearch: boolean,
  globalSearchQuery: string
): 'search' | 'firstRow' | undefined {
  if (globalSearchQuery.trim()) {
    return undefined;
  }
  if (!showSectionSearch) {
    return 'firstRow';
  }
  return 'search';
}

interface FilterCheckboxListRowProps {
  option: CheckboxFilterOption;
  index: number;
  isSelected: boolean;
  isFocused: boolean;
  tabIndex: number;
  onToggle: (key: string) => void;
  onRowKeyDown: (index: number, e: KeyboardEvent<HTMLDivElement>) => void;
  rowRef: (el: HTMLDivElement | null) => void;
  tagVariant?: 'cluster' | 'instance' | 'volume' | 'node' | 'default';
  showCount?: boolean;
}

const FilterCheckboxListRow = memo(function FilterCheckboxListRow({
  option,
  index,
  isSelected,
  isFocused,
  tabIndex,
  onToggle,
  onRowKeyDown,
  rowRef,
  tagVariant,
  showCount = true,
}: FilterCheckboxListRowProps) {
  const handleToggle = useCallback(() => onToggle(option.key), [onToggle, option.key]);
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => onRowKeyDown(index, e),
    [onRowKeyDown, index]
  );

  return (
    <SearchFilterOptionRow
      ref={rowRef}
      label={option.label}
      count={showCount ? option.count : undefined}
      color={option.color}
      tagVariant={tagVariant}
      isSelected={isSelected}
      isFocused={isFocused}
      tabIndex={tabIndex}
      onToggle={handleToggle}
      onKeyDown={handleKeyDown}
    />
  );
});

export default function FilterCheckboxList({
  options,
  globalSearchQuery = '',
  showSectionSearch = false,
  isDisclosureOpen = false,
  isSelected,
  onToggle,
  tagVariant,
  showCount = true,
  sectionSearchInputId,
  sectionSearchAriaLabel,
}: FilterCheckboxListProps) {
  const [sectionSearchQuery, setSectionSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const visible = useMemo(
    () => filterOptionsByMergedQuery(options, globalSearchQuery, sectionSearchQuery),
    [options, globalSearchQuery, sectionSearchQuery]
  );

  const hasActiveSearch = Boolean(globalSearchQuery.trim() || sectionSearchQuery.trim());

  const onActivate = useCallback(
    (index: number) => onToggle(visible[index].key),
    [onToggle, visible]
  );

  const { focusedIndex, getRowRef, handleRowKeyDown, handleSearchKeyDown } = useFilterListKeyboardNav({
    itemCount: visible.length,
    onActivate,
    searchInputRef: showSectionSearch ? searchInputRef : undefined,
    isDisclosureOpen,
    focusOnOpen: getFilterListFocusOnOpen(showSectionSearch, globalSearchQuery),
  });

  const list = (
    <FilterScrollableList>
      {visible.length > 0 ? (
        visible.map((option, index) => (
          <FilterCheckboxListRow
            key={option.key}
            option={option}
            index={index}
            isSelected={isSelected(option.key)}
            isFocused={focusedIndex === index}
            tabIndex={focusedIndex === index ? 0 : -1}
            onToggle={onToggle}
            onRowKeyDown={handleRowKeyDown}
            rowRef={getRowRef(index)}
            tagVariant={tagVariant}
            showCount={showCount}
          />
        ))
      ) : hasActiveSearch ? (
        <Typography className="p-2 text-center text-gray-300" variant="caption">
          No results found
        </Typography>
      ) : null}
    </FilterScrollableList>
  );

  if (!showSectionSearch) {
    return <div className="-mt-3">{list}</div>;
  }

  return (
    <div className="flex flex-col relative overflow-auto flex-1">
      <div
        className="relative z-10 bg-white"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {isDisclosureOpen && (
          <SearchBar
            ref={searchInputRef}
            inputId={sectionSearchInputId}
            aria-label={sectionSearchAriaLabel}
            size="md-responsive"
            placeholder="Search"
            value={sectionSearchQuery}
            onChange={(e) => setSectionSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="w-full"
          />
        )}
      </div>
      {list}
    </div>
  );
}

export function hasVisibleFilterOptions(
  options: CheckboxFilterOption[],
  searchQuery: string
): boolean {
  return filterOptionsByQuery(options, searchQuery).length > 0;
}
